import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { CoachNotFound } from "~/components/coach-not-found";
import { CoachSiteChrome } from "~/components/layouts/coach-site-chrome";
import { authMiddleware } from "~/lib/auth-middleware";
import { permissionMiddleware } from "~/lib/permission-middleware";
import type { CoachMatchingDebugData } from "~/lib/schemas/coach-matching-debug";
import { isCoachIndexable } from "~/lib/shared/coach-indexability";
import { canonicalUsernameCandidate } from "~/lib/shared/utils/username";

// Re-export the shared checkout function for this route
export { createCheckout } from "~/lib/checkout/create-checkout";

export const LeadMagnetFormSchema = z.object({
  coachId: z.string().uuid(),
});

export type LeadMagnetForm = z.infer<typeof LeadMagnetFormSchema>;

export const submitLeadAction = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(LeadMagnetFormSchema)
  .handler(async ({ data, context }) => {
    const { submitLeadMagnetDownload } =
      await import("~/lib/server/lead-magnet");
    return submitLeadMagnetDownload(context.user, data.coachId);
  });

export const getResourceCount = createServerFn({ method: "GET" })
  .validator(z.object({ username: z.string() }))
  .handler(async ({ data }) => {
    const { username } = data;
    const { getCoachByUsername } = await import("~/lib/server/coach");
    const { isCoachHiddenFromRequest } =
      await import("~/lib/server/coach-visibility");
    const coach = await getCoachByUsername(username);

    if (!coach || (await isCoachHiddenFromRequest(coach))) {
      return 0;
    }

    const { getDBWithInit } = await import("~/lib/db");
    const { count, eq } = await import("drizzle-orm");
    const db = await getDBWithInit();
    const { resources } = await import("~/../drizzle/schema");
    const result = await db
      .select({ value: count() })
      .from(resources)
      .where(eq(resources.coachId, coach.id));

    return result[0]?.value || 0;
  });

export const getCoachProfile = createServerFn({ method: "GET" })
  .validator(z.object({ username: z.string() }))
  .handler(async ({ data }) => {
    const { username } = data;
    const { getPublicCoachProfile } =
      await import("~/lib/server/coach-visibility");
    return getPublicCoachProfile(username);
  });

/** Exposes private coach attributes (age, gender, eligibility), so admins only. */
export const getCoachMatchingDebugData = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(z.object({ username: z.string() }))
  .handler(async ({ data }): Promise<CoachMatchingDebugData | null> => {
    const { username } = data;
    const { getDBWithInit } = await import("~/lib/db");
    const { eq } = await import("drizzle-orm");
    const { coachExpertise, expertiseSubcategories, expertiseCategories } =
      await import("~/../drizzle/schema");
    const { CoachEmbeddingType, ExpertiseType } =
      await import("~/lib/schemas/db-enums");

    const db = await getDBWithInit();

    // Find coach by username
    const coach = await db.query.coaches.findFirst({
      where: { username },
      columns: {
        id: true,
        profilePictureUrl: true,
        ghlUserId: true,
        isFoundingCoach: true,
        isFeaturedCoach: true,
      },
    });

    if (!coach) {
      return null;
    }

    // Fetch coach details
    const details = await db.query.coachDetails.findFirst({
      where: { coachId: coach.id },
    });

    // Fetch profile
    const profile = await db.query.profiles.findFirst({
      where: { coachId: coach.id },
      columns: {
        isPublic: true,
        isPreGeneratedProfile: true,
        primaryCity: true,
        closestMajorCity: true,
        primaryCountry: true,
        languages: true,
      },
    });

    // Unclaimed pre-generated profiles are invisible to the public (#1014)
    const { isCoachHiddenFromRequest } =
      await import("~/lib/server/coach-visibility");
    if (await isCoachHiddenFromRequest({ id: coach.id, profile })) {
      return null;
    }

    // Check if embedding exists
    const embedding = await db.query.coachEmbeddings.findFirst({
      where: {
        coachId: coach.id,
        type: CoachEmbeddingType.PreferencesMatch,
      },
      columns: { id: true },
    });

    // Fetch expertise with category names
    const expertiseData = await db
      .select({
        id: expertiseSubcategories.id,
        name: expertiseSubcategories.name,
        categoryName: expertiseCategories.name,
        type: coachExpertise.type,
      })
      .from(coachExpertise)
      .innerJoin(
        expertiseSubcategories,
        eq(coachExpertise.subcategoryId, expertiseSubcategories.id),
      )
      .innerJoin(
        expertiseCategories,
        eq(expertiseSubcategories.categoryId, expertiseCategories.id),
      )
      .where(eq(coachExpertise.coachId, coach.id));

    const primaryExpertise = expertiseData
      .filter((e) => e.type === ExpertiseType.Primary)
      .map((e) => ({
        id: Number(e.id),
        name: e.name,
        categoryName: e.categoryName,
      }));

    const secondaryExpertise = expertiseData
      .filter((e) => e.type === ExpertiseType.Secondary)
      .map((e) => ({
        id: Number(e.id),
        name: e.name,
        categoryName: e.categoryName,
      }));

    // Calculate age from DOB
    let age: number | null = null;
    if (details?.dateOfBirth) {
      const today = new Date();
      const birthDate = new Date(details.dateOfBirth);
      age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
    }

    return {
      eligibility: {
        hasProfilePhoto: !!coach.profilePictureUrl,
        hasGhlIntegration: !!coach.ghlUserId,
        isPublic: profile?.isPublic ?? false,
      },
      demographics: {
        gender: details?.gender ?? null,
        age,
        primaryCity: profile?.primaryCity ?? null,
        closestMajorCity: profile?.closestMajorCity ?? null,
        primaryCountry: profile?.primaryCountry ?? null,
        languages: profile?.languages ?? null,
      },
      expertise: {
        primary: primaryExpertise,
        secondary: secondaryExpertise,
      },
      coaching: {
        formats: details?.coachingFormats ?? [],
        sessionDeliveryMethods: details?.sessionDeliveryMethods ?? [],
        coachingApproaches: details?.coachingApproaches ?? [],
        experienceLevel: details?.experienceLevel ?? null,
      },
      faith: details?.faithIntegration ?? [],
      personality: {
        discType: details?.discType ?? null,
        mbtiType: details?.mbtiType ?? null,
        enneagramType: details?.enneagramType ?? null,
        humanDesignType: details?.humanDesignType ?? null,
      },
      status: {
        isFoundingCoach: coach.isFoundingCoach ?? false,
        isFeaturedCoach: coach.isFeaturedCoach ?? false,
      },
      hasEmbedding: !!embedding,
    };
  });

export const Route = createFileRoute("/$username/_profile")({
  loader: async ({ context, params }) => {
    // Pre-load and return coach profile and resource count for children & head
    const [coach, resourceCount] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["coach-profile", params.username],
        queryFn: () => getCoachProfile({ data: { username: params.username } }),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["resource-count", params.username],
        queryFn: () =>
          getResourceCount({ data: { username: params.username } }),
      }),
    ]);

    if (!coach) {
      // Articles and other off-platform content routinely link to a coach as
      // `/first-last`, or the accented display form, when the real username is
      // `/firstlast`. Only reached on a miss, and only redirects once the
      // folded spelling is confirmed to belong to a real coach — hand-picked
      // usernames may contain hyphens, so the verbatim lookup above wins.
      const candidate = canonicalUsernameCandidate(params.username);
      if (candidate) {
        const candidateCoach = await context.queryClient.ensureQueryData({
          queryKey: ["coach-profile", candidate],
          queryFn: () => getCoachProfile({ data: { username: candidate } }),
        });
        if (candidateCoach) {
          // 302, not 301 — unlike the static RENAMED_COACH_USERNAMES map in
          // `$username.tsx`, this destination is derived from live data. A
          // hyphenated username is assignable (the admin schema allows
          // `a-b-c`, and uniqueness is only an exact-match DB constraint), so
          // `jane-doe` could later belong to a different coach than `janedoe`.
          // A 301 caches in browsers indefinitely and would keep sending those
          // visitors past the verbatim lookup that is supposed to win — an
          // un-revocable commitment we can't make on mutable state.
          throw redirect({
            to: "/$username",
            params: { username: candidate },
            statusCode: 302,
          });
        }
      }
      throw notFound();
    }

    return { coach, resourceCount };
  },
  head: ({ loaderData }) => {
    const shouldIndex =
      !!loaderData?.coach && isCoachIndexable(loaderData.coach);

    if (!shouldIndex) {
      return {
        meta: [{ name: "robots", content: "noindex, nofollow" }],
      };
    }

    return {};
  },
  notFoundComponent: () => {
    const { username } = Route.useParams();
    return <CoachNotFound username={username} />;
  },
  component: ProfileLayout,
});

function ProfileLayout() {
  return (
    <CoachSiteChrome>
      <Outlet />
    </CoachSiteChrome>
  );
}
